import React, { useEffect } from 'react';
import { useLocale, useLocalization } from '../../context/Localization/localization';
import { useRecallsState } from '../../context/recalls';
import { CTAButton } from '../../controls/ButtonBase';
import { DetailCard } from '../../controls/DetailCard/DetailCard';
import { DetailCardList } from '../../controls/DetailCard/DetailCardList';
import { Heading } from '../../controls/Heading/Heading';
import { Spinner } from '../../controls/Spinner/Spinner';
import { ScrollableBody } from '../Layout/ScrollableBody';
import css from './Recalls.module.scss';
import { useBackgroundDataState } from '../../context/backgroundData';
import { Spacer } from '../../controls/Spacer/Spacer';
import dayjs from 'dayjs';
import { ServiceCardList } from '../../controls/ServiceCard/ServiceCard';
import { useCart } from '../../context/cart';
import { useKioskState } from '../../context/Kiosk/kiosk';
import { useAppointmentState, useAppointmentUpdateAndPersist } from '../../context/Appointment/appointment';
import { getUpdateAptRndServices } from '../../helpers/Appointment';
import { GMButtonGroup } from '../../controls/ButtonGroup';
import { GMAppointmentName } from '../../controls/AppointmentName';
import { GMTitle } from '../../controls/Title';
import { Locales } from '../../context/Localization/localization.types';
import { ServiceCardListItem } from '../../controls/ServiceCardListItem/ServiceCardListItem';

interface IProps {
    onProceed: () => void;
    onSkip: () => void;
}

export const RecallsRecommendedLastVisit: React.FC<IProps> = React.memo(({ onSkip, onProceed }) => {
    const Localization = useLocalization();
    const { loading, recalls } = useRecallsState();
    const { loadingRndServices, rndServicesInitialized, rndServices, rndLastVisit } = useBackgroundDataState();
    const {
        dealer: { recommendedNotDoneEnabled, recallsActive },
    } = useKioskState();
    const setAppointment = useAppointmentUpdateAndPersist();
    const appointment = useAppointmentState();
    const locale = useLocale();
    const cart = useCart();

    useEffect(() => {
        if (!loading && recalls.length === 0 && !loadingRndServices && rndServicesInitialized && rndServices.length === 0) {
            onSkip();
        }
    }, [onSkip, loading, recalls, loadingRndServices, rndServicesInitialized, rndServices]);

    const onProceedClick = async () => {
        if (recommendedNotDoneEnabled) {
            const rndServicesRecommended = appointment.rndServicesRecommended || [];
            rndServices.forEach((rnd) => !rndServicesRecommended.includes(rnd.opCode) && rndServicesRecommended.push(rnd.opCode));

            await setAppointment({
                ...appointment,
                rndServices: getUpdateAptRndServices(cart.rndServices),
                rndServicesRecommended: rndServicesRecommended,
            });
        }

        onProceed();
    };

    return (
        <ScrollableBody
            heading={
                <Heading
                    primaryHeading={
                        <GMAppointmentName />
                    }
                    secondaryHeading={Localization.WeHaveTheFollowingInfo}
                    leftAlign
                />
            }
            proceedButtons={
                <GMButtonGroup
                    buttons={[
                        <CTAButton
                            styleType={'primary'}
                            disabled={loading || loadingRndServices}
                            onClick={onProceedClick}
                            label={Localization.Proceed}
                            id={Localization.Proceed}
                            ariaLabel={Localization.Proceed}
                        />,
                    ]}
                />
            }
            topDivider
        >
            {!loading && !loadingRndServices && rndServicesInitialized && (recalls.length > 0 || rndServices.length > 0) ? (
                <>
                    {recallsActive ? (
                        <div>
                            <GMTitle headerLevel={4}>{Localization.RecallsDetected}</GMTitle>
                            {recalls.length > 0 ? (
                                <>
                                    <div className={css.subheading}>{Localization.WeDetectedRecalls}</div>
                                    <DetailCardList>
                                        {recalls.map((recall, index) => (
                                            <DetailCard
                                                name={recall.title}
                                                id={recall.title}
                                                label={''}
                                                ariaLabel={recall.title}
                                                key={recall.title + recall.description}
                                                title={recall.title}
                                                index={(index + 1).toString()}
                                                details={recall.description}
                                            />
                                        ))}
                                    </DetailCardList>
                                    <div className={css.notice}>
                                        <p>{Localization.ServiceAdvisorReviewReacalls}</p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className={css.subheading}>{Localization.NoRecallsDetected}</div>
                                    <br />
                                </>
                            )}
                        </div>
                    ) : null}
                    {rndServices.length > 0 ? (
                        <>
                            {recallsActive && <Spacer />}
                            <div>
                                <GMTitle headerLevel={4}>{Localization.RecommendedLastVisit}</GMTitle>
                                <div className={css.subheading}>
                                    {rndLastVisit
                                        ? Localization.formatString(
                                            Localization.TheseAreServiceWeRecommendDate,
                                            dayjs(rndLastVisit)
                                                .locale(locale === Locales.es ? 'es-us' : locale === Locales.fr ? 'fr' : 'en')
                                                .format(
                                                    locale === Locales.es ? 'DD [de] MMMM [de] YYYY' : locale === Locales.fr ? 'DD MMMM YYYY' : 'MMMM DD, YYYY',
                                                ),
                                        )
                                        : Localization.TheseAreServiceWeRecommend}
                                </div>
                                <br />
                                <div className={css.subheading}>{Localization.PleaseSelectAnyItemRND}</div>

                                <ServiceCardList>
                                    {rndServices.map((rnd, index) => {
                                        return (
                                            <ServiceCardListItem
                                                rndService={rnd}
                                                index={index}
                                            />
                                        );
                                    })}
                                </ServiceCardList>
                            </div>
                        </>
                    ) : null}
                </>
            ) : (
                <Spinner />
            )}
        </ScrollableBody>
    );
});
